import React, { useMemo } from 'react'

import {
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  CreateButton,
  Datagrid,
  ImageField,
  List,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  SelectInput,
  TextField,
  TopToolbar,
  Translate,
  useTranslate,
} from 'react-admin'

import { Tooltip } from '@mantine/core'
import { LXNumberField } from '../../components/LXNumberField'
import { API } from '../../helpers/Api.helper'
import { ListHelper } from '../../helpers/List.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Forbidden } from '../Forbidden'

type YoutubeChannelListActionsProps = {
  isAuthorised: boolean
  translate: Translate
}
const YoutubeChannelListActions = ({
  isAuthorised,
  translate,
}: YoutubeChannelListActionsProps): JSX.Element => (
  <TopToolbar>
    <Tooltip
      label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
        'pages.users.permissionsList.youtubeChannels.edit',
      )}`}
      hidden={isAuthorised}
    >
      <span>
        <CreateButton disabled={!isAuthorised} />
      </span>
    </Tooltip>
  </TopToolbar>
)

export const YoutubechannelList: React.FC = props => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const youtubeCmsList = useMemo(() => userStore.getYoutubeCMSList(), [userStore])

  if (!userStore.isAuthorised('youtubeChannels.read')) {
    return <Forbidden permission="youtubeChannels.read" />
  }

  return (
    <List
      {...props}
      actions={
        <YoutubeChannelListActions
          isAuthorised={userStore.isAuthorised('youtubeChannels.edit')}
          translate={translate}
        />
      }
      sort={{ field: 'name', order: 'ASC' }}
      perPage={API.DEFAULT_PAGINATION.perPage}
      exporter={false}
      filterDefaultValues={{ isOwnedByLogicomix: true }}
      filters={[
        <SearchInput key="search" source="q" alwaysOn />,
        <SelectInput key="cms" source="cms" choices={youtubeCmsList} alwaysOn />,
        <ReferenceInput
          key="defaultBeneficiaryId"
          source="defaultBeneficiaryId"
          reference="beneficiaries"
          alwaysOn
          allowEmpty={false}
          sort={ListHelper.DEFAULT_SORT}
        >
          <AutocompleteInput optionText="name" label="_generics.beneficiary" />
        </ReferenceInput>,
        <BooleanInput
          key="isOwnedByLogicomix"
          source="isOwnedByLogicomix"
          alwaysOn
          label={translate('pages.youtubeChannels.isOwnedByLogicomix')}
        />,
      ]}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <ImageField source="thumbnailUrl" sortable={false} label={''} />
        <TextField source="name" label={translate('_generics.name')} />
        <LXNumberField
          source="subscribersCount"
          format="large"
          label={translate('pages.youtubeChannels.subscribersCount')}
          hideLabel
        />
        <LXNumberField
          source="videosCount"
          format="large"
          label={translate('pages.youtubeChannels.videosCount')}
          hideLabel
        />
        <LXNumberField
          source="viewsCount"
          format="large"
          label={translate('pages.youtubeChannels.viewsCount')}
          hideLabel
        />
        <TextField source="cms" sortable={false} label={translate('pages.youtubeChannels.cms')} />
        <BooleanField
          source="isOwnedByLogicomix"
          sortable={false}
          label={translate('pages.youtubeChannels.isOwnedByLogicomix')}
        />
        <ReferenceField
          source="defaultBeneficiaryId"
          reference="beneficiaries"
          label={translate('pages.youtubeChannels.defaultBeneficiary')}
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          source="defaultProductId"
          reference="products"
          label={translate('pages.youtubeChannels.defaultProduct')}
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
      </Datagrid>
    </List>
  )
}
